import React from 'react';
import PropTypes from 'prop-types';
import { Col, Input, Label, Row } from 'reactstrap';
import api from '../../../constants/api';
import { useLocaleConfig } from '../../../context/AppContext';
import ActionButton from '../../common/ActionButton';
import AppButton from '../../common/AppButton';
import DataTable from '../../common/DataTable';
import { isApiSuccess } from '../../../constants/apiResult';
import MultiSelectDropdown from '../../common/MultiSelectDropdown';

const makeUid = () => Math.floor(10000 + Math.random() * 90000);

// Price of an additional service for the chosen car type.
const addonPrice = (addon, carType) =>
  parseFloat(carType === 'salon' ? addon?.sa_price_salon_en : addon?.sa_price_fourwheel_en) || 0;

const addonDuration = (addon) => parseInt(addon?.sa_duration, 10) || 0;

export default function QuotationServices({
  selectedRows,
  onRowsChange,
  totalPrice,
  sparePartsCost,
  editQuotation = false,
  quotationId,
  quotePrice,
  disabled = false,
  toast,
}) {
  QuotationServices.propTypes = {
    selectedRows: PropTypes.array,
    onRowsChange: PropTypes.func,
    totalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sparePartsCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    editQuotation: PropTypes.bool,
    quotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quotePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    toast: PropTypes.object,
  };

  const { formatCurrency } = useLocaleConfig();
  const [categories, setCategories] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [additionalServices, setAdditionalServices] = React.useState([]);
  const [categoryId, setCategoryId] = React.useState('');
  const [serviceId, setServiceId] = React.useState('');
  const [additionalIds, setAdditionalIds] = React.useState([]);
  const [loadingAdditional, setLoadingAdditional] = React.useState(false);
  const [carType, setCarType] = React.useState('four-wheeler');
  const [carNo, setCarNo] = React.useState('');
  const [servicePrice, setServicePrice] = React.useState('');
  const [serviceDuration, setServiceDuration] = React.useState('');
  const [editServiceCarNo, setEditServiceCarNo] = React.useState('');
  const [editServicePrice, setEditServicePrice] = React.useState('');

  React.useEffect(() => {
    api
      .get('/slotbooking/getServiceTypes')
      .then((response) => {
        setCategories(response?.data?.data || []);
      })
      .catch((error) => console.error('Unable to load quotation categories', error));
  }, [editQuotation]);

  React.useEffect(() => {
    setAdditionalIds([]);

    if (!serviceId) {
      setAdditionalServices([]);
      return undefined;
    }

    let active = true;
    setLoadingAdditional(true);
    api
      .get('/service/getServiceAddons', { params: { serviceId } })
      .then((response) => {
        if (active) setAdditionalServices(response?.data?.data || []);
      })
      .catch((error) => {
        console.error('Unable to load quotation additional services', error);
        if (active) setAdditionalServices([]);
      })
      .finally(() => {
        if (active) setLoadingAdditional(false);
      });
    return () => {
      active = false;
    };
  }, [serviceId]);

  const selectedCategory = categories.find(
    (item) => String(item.service_category_id) === String(categoryId),
  );
  const selectedService = services.find(
    (item) => String(item.sp_id) === String(serviceId),
  );
  // Ticked add-ons, priced for the current car type.
  const selectedAdditionals = additionalServices
    .filter((item) => additionalIds.includes(String(item.service_add_id)))
    .map((item) => ({
      id: item.service_add_id,
      name: item.sa_name_en,
      price: addonPrice(item, carType),
      time: addonDuration(item),
    }));
  const additionalPrice = selectedAdditionals.length
    ? selectedAdditionals.reduce((sum, item) => sum + item.price, 0).toFixed(2)
    : '';
  const additionalDuration = selectedAdditionals.length
    ? String(selectedAdditionals.reduce((sum, item) => sum + item.time, 0))
    : '';
  const additionalOptions = additionalServices.map((item) => ({
    value: String(item.service_add_id),
    label: item.sa_name_en,
    meta: `${formatCurrency(addonPrice(item, carType))} · ${addonDuration(item)} min`,
  }));

  const loadServices = (nextCategoryId) => {
    setCategoryId(nextCategoryId);
    setServiceId('');
    setServicePrice('');
    setServiceDuration('');
    setServices([]);
    setAdditionalIds([]);
    setAdditionalServices([]);

    if (!nextCategoryId) return;

    api
      .get('/slotbooking/getServicesByType', {
        params: { serviceTypeId: nextCategoryId },
      })
      .then((response) => setServices(response?.data?.data || []))
      .catch((error) => {
        console.error('Unable to load quotation services', error);
        setServices([]);
      });
  };

  const updateService = (nextServiceId, nextCarType = carType) => {
    setServiceId(nextServiceId);
    const service = services.find(
      (item) => String(item.sp_id) === String(nextServiceId),
    );
    setServicePrice(
      service
        ? nextCarType === 'salon'
          ? service.sp_salon_price_en
          : service.sp_four_price_en
        : '',
    );
    setServiceDuration(service?.sp_estimated_time || '');
    setEditServicePrice(
      service
        ? nextCarType === 'salon'
          ? service.sp_salon_price_en
          : service.sp_four_price_en
        : '',
    );
  };

  const handleCarTypeChange = (event) => {
    const nextCarType = event.target.value;
    setCarType(nextCarType);
    if (serviceId) updateService(serviceId, nextCarType);
  };

  const addServicesToEditQuotation = () => {
    if (!selectedService || !editServicePrice || !quotationId) {
      if (toast) toast.error('Please select a service and price');
      return;
    }

    let userObject;
    try {
      userObject = JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      userObject = {};
    }

    const rowsToSave = [{
      name: selectedService.sp_name_en,
      id: selectedService.sp_id,
      price: parseFloat(editServicePrice) || 0,
      car_type: carType,
      car_no: editServiceCarNo,
      time: parseInt(serviceDuration, 10) || 0,
      salon: selectedService.sp_salon_price_en,
      fourwheel: selectedService.sp_four_price_en,
    }];

    // Each add-on is its own row, linked to the main service it extends.
    selectedAdditionals.forEach((addon) => {
      rowsToSave.push({
        name: addon.name,
        id: addon.id,
        price: addon.price,
        car_type: carType,
        car_no: editServiceCarNo,
        time: addon.time,
        add_service_type: 'addon',
        add_service_parent_id: selectedService.sp_id,
      });
    });

    const updatedQuoteTotal = (parseFloat(quotePrice) || 0)
      + rowsToSave.reduce((sum, row) => sum + row.price, 0);

    rowsToSave
      .reduce(
        (request, row) => request.then(() => api
          .post('/quotation/addServiceToBooking', {
            ...row,
            booking_id: quotationId,
            added_by: userObject.user_retrive_name || '',
            total_price: updatedQuoteTotal,
          })
          .then((response) => {
            if (!isApiSuccess(response)) throw new Error('Unable to add Service');
          })),
        Promise.resolve(),
      )
      .then(() => {
        if (toast) toast.success('Service added');
        window.location.reload();
      })
      .catch((error) => {
        console.error('Unable to add quotation service', error);
        if (toast) toast.error('Unable to add Service');
      });
  };

  const clearSelection = () => {
    setServiceId('');
    setAdditionalIds([]);
    setServicePrice('');
    setServiceDuration('');
    setCarNo('');
  };

  const addSelectedServices = () => {
    if (!selectedService) {
      if (toast) toast.error('Please select a service');
      return;
    }

    const rows = [];
    if (selectedService) {
      rows.push({
        type: 'main',
        category: selectedCategory?.service_category_name_en || '',
        name: selectedService.sp_name_en,
        id: selectedService.sp_id,
        price: parseFloat(servicePrice) || 0,
        uid: `${selectedService.sp_id}-${makeUid()}`,
        car_type: carType,
        car_no: carNo.trim(),
        time: parseInt(serviceDuration, 10) || 0,
      });
    }

    selectedAdditionals.forEach((addon) => {
      rows.push({
        type: 'additional',
        category: selectedCategory?.service_category_name_en || '',
        service: selectedService.sp_name_en,
        parent_id: selectedService.sp_id,
        name: addon.name,
        id: addon.id,
        price: addon.price,
        uid: `additional-${addon.id}-${makeUid()}`,
        car_type: carType,
        car_no: carNo.trim(),
        time: addon.time,
      });
    });

    onRowsChange([...(selectedRows || []), ...rows]);
    clearSelection();
  };

  const deleteSelectedRow = (row) => {
    onRowsChange((selectedRows || []).filter((item) => item.uid !== row.uid));
  };

  const estimatedTime = (selectedRows || []).reduce(
    (sum, row) => sum + (parseInt(row.time, 10) || 0),
    0,
  );

  if (editQuotation) {
    return (
      <div className="edit-quote-quotation-services">
        <Row className="edit-quote-quotation-services-row">
          <Col lg={3} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-service-category">
                Category<span className="req">*</span>
              </Label>
              <Input
                id="edit-quote-service-category"
                type="select"
                value={categoryId}
                onChange={(event) => loadServices(event.target.value)}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.service_category_id} value={category.service_category_id}>
                    {category.service_category_name_en}
                  </option>
                ))}
              </Input>
            </div>
          </Col>
          <Col lg={3} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-service-select">Service <span className="req">*</span></Label>
              <Input
                id="edit-quote-service-select"
                type="select"
                value={serviceId}
                disabled={!categoryId}
                onChange={(event) => updateService(event.target.value)}
              >
                <option value="">Select Service</option>
                {services.map((service) => (
                  <option key={service.sp_id} value={service.sp_id}>{service.sp_name_en}</option>
                ))}
              </Input>
            </div>
          </Col>
          <Col lg={3} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-additional-service-toggle">Additional Services</Label>
              <MultiSelectDropdown
                id="edit-quote-additional-service"
                options={additionalOptions}
                selected={additionalIds}
                onChange={setAdditionalIds}
                placeholder={serviceId ? 'Select Additional Services' : 'Select a service first'}
                emptyText="This service has no additional services."
                loading={loadingAdditional}
                disabled={!categoryId || !serviceId}
              />
            </div>
          </Col>
          <Col lg={3} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-service-car-type">Car Type</Label>
              <Input
                id="edit-quote-service-car-type"
                type="select"
                value={carType}
                onChange={(event) => {
                  const nextType = event.target.value;
                  setCarType(nextType);
                  if (serviceId) updateService(serviceId, nextType);
                }}
              >
                <option value="four-wheeler">Four Wheeler</option>
                <option value="salon">Salon</option>
              </Input>
            </div>
          </Col>
        </Row>

        <Row className="edit-quote-quotation-services-row">
          <Col lg={2} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-service-price">Service Price</Label>
              <Input id="edit-quote-service-price" value={editServicePrice} disabled={!serviceId}
                onChange={(event) => setEditServicePrice(event.target.value)} />
            </div>
          </Col>
          <Col lg={2} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-service-duration">Service Duration</Label>
              <Input id="edit-quote-service-duration" value={serviceDuration} disabled />
            </div>
          </Col>
          <Col lg={2} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-additional-price">Additional Price</Label>
              <Input id="edit-quote-additional-price" value={additionalPrice} disabled />
            </div>
          </Col>
          <Col lg={2} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-additional-duration">Additional Duration</Label>
              <Input id="edit-quote-additional-duration" value={additionalDuration} disabled />
            </div>
          </Col>
          <Col lg={2} md={6}>
            <div className="bk-field">
              <Label htmlFor="edit-quote-service-car-no">Car No</Label>
              <Input id="edit-quote-service-car-no" value={editServiceCarNo}
                onChange={(event) => setEditServiceCarNo(event.target.value)} />
            </div>
          </Col>
          <Col lg={2} md={6} className="edit-quote-quotation-services-add">
            <AppButton label="Add" variant="buttonColor" type="button" disabled={disabled}
              onClick={addServicesToEditQuotation} />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="quotation-services">
      <Row className="quotation-services-fields">
        <Col lg={3} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-service-category">
              Category<span className="req">*</span>
            </Label>
            <Input
              id="quotation-service-category"
              type="select"
              value={categoryId}
              onChange={(event) => loadServices(event.target.value)}
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option
                  key={category.service_category_id}
                  value={category.service_category_id}
                >
                  {category.service_category_name_en}
                </option>
              ))}
            </Input>
          </div>
        </Col>
        <Col lg={3} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-service-select">
              Service<span className="req">*</span>
            </Label>
            <Input
              id="quotation-service-select"
              type="select"
              value={serviceId}
              disabled={!categoryId}
              onChange={(event) => updateService(event.target.value)}
            >
              <option value="">Select Service</option>
              {services.map((service) => (
                <option key={service.sp_id} value={service.sp_id}>
                  {service.sp_name_en}
                </option>
              ))}
            </Input>
          </div>
        </Col>
        <Col lg={3} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-additional-service-toggle">Additional Services</Label>
            <MultiSelectDropdown
              id="quotation-additional-service"
              options={additionalOptions}
              selected={additionalIds}
              onChange={setAdditionalIds}
              placeholder={serviceId ? 'Select Additional Services' : 'Select a service first'}
              emptyText="This service has no additional services."
              loading={loadingAdditional}
              disabled={!categoryId || !serviceId}
            />
          </div>
        </Col>
        <Col lg={3} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-car-type">Car Type</Label>
            <Input
              id="quotation-car-type"
              type="select"
              value={carType}
              onChange={handleCarTypeChange}
            >
              <option value="four-wheeler">Four Wheeler</option>
              <option value="salon">Salon</option>
            </Input>
          </div>
        </Col>
      </Row>

      <Row className="quotation-services-fields quotation-services-fields-details">
        <Col lg={2} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-service-price">Service Price</Label>
            <Input id="quotation-service-price" value={servicePrice} disabled />
          </div>
        </Col>
        <Col lg={2} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-service-duration">Service Duration</Label>
            <Input id="quotation-service-duration" value={serviceDuration} disabled />
          </div>
        </Col>
        <Col lg={2} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-additional-price">Additional Price</Label>
            <Input id="quotation-additional-price" value={additionalPrice} disabled />
          </div>
        </Col>
        <Col lg={2} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-additional-duration">Additional Duration</Label>
            <Input id="quotation-additional-duration" value={additionalDuration} disabled />
          </div>
        </Col>
        <Col lg={2} md={6}>
          <div className="bk-field">
            <Label htmlFor="quotation-car-no">Car No</Label>
            <Input
              id="quotation-car-no"
              value={carNo}
              onChange={(event) => setCarNo(event.target.value)}
            />
          </div>
        </Col>
        <Col lg={2} md={6} className="quotation-services-add-col">
          <AppButton
            label="Add"
            variant="buttonColor"
            onClick={addSelectedServices}
          />
        </Col>
      </Row>

      <div className="quotation-services-summary quotation-services-summary-add">
        <strong>Total Price: {formatCurrency(totalPrice || 0)}</strong>
        <strong>Total Estimated: {estimatedTime} Min</strong>
        <strong>Spare Parts Cost: {formatCurrency(sparePartsCost || 0)}</strong>
      </div>

      <div className="quotation-selected-services">
        <div className="quotation-selected-services-heading">Selected Services</div>
        <div className="table-responsive">
          <DataTable
            columns={[
              { key: 'number', header: 'S.No', render: (row, index) => index + 1 },
              { key: 'category', header: 'Category', render: (row) => row.category || '—' },
              {
                key: 'service',
                header: 'Service',
                render: (row) => row.type === 'additional' ? row.service || '—' : row.name || '—',
              },
              {
                key: 'additional',
                header: 'Additional Service',
                render: (row) => row.type === 'additional' ? row.name || '—' : '—',
              },
              {
                key: 'car_type',
                header: 'Car Type',
                render: (row) => row.car_type === 'salon' ? 'Salon' : 'Four Wheeler',
              },
              { key: 'car_no', header: 'Car No', render: (row) => row.car_no || '—' },
              { key: 'time', header: 'Duration (Min)', render: (row) => row.time || 0 },
              { key: 'price', header: 'Price (INR)', render: (row) => formatCurrency(row.price || 0) },
              {
                key: 'action',
                header: 'Action',
                render: (row) => (
                  <ActionButton action="delete" onClick={() => deleteSelectedRow(row)} />
                ),
              },
            ]}
            data={selectedRows || []}
            searchable={false}
            emptyMessage="No services added yet"
            rowKey={(row) => row.uid}
          />
        </div>
      </div>
    </div>
  );
}
